import { useEffect, useRef, useState, type ReactNode } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useCaseOverview, type CaseOverviewOut, type CaseOverviewPageOut } from "@/api";
import { Button } from "@/components/Button";
import { MeshMark } from "@/components/MeshMark";
import { ScreenHead } from "@/components/ScreenHead";
import { StateMessage } from "@/components/StateMessage";
import { useStageChange } from "@/components/stages/useStageChange";
import { OPEN_STATUSES, STAGE_FILTERS } from "@/lib/stages";
import { useDebouncedValue } from "@/lib/useDebouncedValue";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { useRouteNotice } from "@/lib/useRouteNotice";

import { RegisterTable } from "./RegisterTable";
import { RegisterToolbar, type CustomerChoice, type RegisterSort } from "./RegisterToolbar";
import styles from "./Register.module.css";

export const SEARCH_DELAY_MS = 250;
export const PAGE_SIZE = 50;
/** The overview route's own ceiling (`Query(..., le=500)`) — fix round 2, finding 7. */
export const MAX_LIMIT = 500;

/**
 * The customers the „Kunde" filter offers: those of the current answer,
 * one each, by name. Once one is chosen, the answer holds only that one —
 * so the choice itself is what is offered, beside „Alle Kunden" (plan D-6:
 * there is no endpoint for „customers of my Vorgänge").
 *
 * Two customers under one name are told apart (fix round 4, M4): by e-mail
 * first; if that is shared or missing too, by the first of their Vorgänge
 * in this answer — a number the row beside it shows. Never by an id nobody
 * can read.
 */
export function customersOf(items: readonly CaseOverviewOut[], selected: CustomerChoice | null): CustomerChoice[] {
  if (selected !== null) return [selected];
  const byId = new Map<string, { name: string; email: string | null; caseNo: string }>();
  for (const item of items) {
    const customer = item.customer;
    if (customer !== null && !byId.has(customer.id)) {
      byId.set(customer.id, { name: customer.company ?? customer.displayName, email: customer.email, caseNo: item.caseNo });
    }
  }
  const tally = (key: (entry: { name: string; email: string | null }) => string) => {
    const counts = new Map<string, number>();
    for (const entry of byId.values()) counts.set(key(entry), (counts.get(key(entry)) ?? 0) + 1);
    return (entry: { name: string; email: string | null }) => counts.get(key(entry)) ?? 0;
  };
  const sameName = tally((entry) => entry.name);
  const sameNameAndMail = tally((entry) => `${entry.name}\n${entry.email ?? ""}`);
  return [...byId.entries()]
    .map(([id, entry]) => {
      if (sameName(entry) < 2) return { id, displayName: entry.name };
      const mail = entry.email !== null && entry.email !== "" ? entry.email : null;
      const parts = [entry.name, mail, mail === null || sameNameAndMail(entry) > 1 ? entry.caseNo : null];
      return { id, displayName: parts.filter((part): part is string => part !== null).join(" · ") };
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName, "de"));
}

interface CountFilters {
  q: string;
  stageLabel: string | null;
  customerName: string | null;
  showFinished: boolean;
  /** The server's count of truly open Vorgänge (fix round 1, I3) — used only when nothing is filtered. */
  openCount: number | null;
}

/**
 * The status line's count (UX ruling 4, F-13): „7 offene Vorgänge",
 * „12 Vorgänge, beendete eingeschlossen", „3 Treffer für „Beispiel“ · In
 * Kalkulation". The count for a search or a filter is the server's
 * `totalCount` on that answer — hits before the limit, not rows on screen.
 * The unfiltered count is a **different** number from the footer's: the
 * default overview answer keeps `won`/`lost` (it only drops the three
 * finished statuses), so it is asked for separately, over `OPEN_STATUSES`
 * (fix round 1, I3) — a decided Vorgang is not „offen". Fix round 2, ruling
 * b: rather than print only the open count and leave the footer's larger
 * „N von M" looking like a second, disagreeing answer, a decided remainder
 * is named too.
 */
export function countLine(page: CaseOverviewPageOut, { q, stageLabel, customerName, showFinished, openCount }: CountFilters): string {
  const n = page.totalCount;
  if (q !== "" || stageLabel !== null || customerName !== null) {
    const head = `${n} Treffer${q !== "" ? ` für „${q}“` : ""}`;
    return [head, stageLabel, customerName].filter((part): part is string => part !== null).join(" · ");
  }
  if (showFinished) return `${n} ${n === 1 ? "Vorgang" : "Vorgänge"}, beendete eingeschlossen`;
  // While the open count has not answered yet, `n` is the nearest honest
  // stand-in — the two agree whenever nothing on the page is won or lost.
  const open = openCount ?? n;
  const decided = openCount !== null ? Math.max(0, n - openCount) : 0;
  if (decided > 0) return `${open} offen · ${decided} entschieden`;
  return open === 1 ? "1 offener Vorgang" : `${open} offene Vorgänge`;
}

/** A customer filter as held: chosen from the list with its name, or by URL with its id alone. */
type CustomerFilter = { id: string; displayName: string | null };

/**
 * `?kunde=<id>` — the register filtered to one customer, the way the record's
 * „Weitere Vorgänge" opens it (UX gate round 1, #8). Read once, as the
 * filter's first value; the filter is the person's from then on. The id
 * only (final review of Plan D): the name comes from the answer
 * (`namedFilter`), never from an address.
 */
function customerFromUrl(params: URLSearchParams): CustomerFilter | null {
  const id = params.get("kunde");
  return id ? { id, displayName: null } : null;
}

/**
 * The filter with a name to show: its own, or — for one opened by URL — the
 * name its Vorgänge in the answer carry (the answer holds only this
 * customer's). „Kunde" while none of them names it.
 */
function namedFilter(filter: CustomerFilter | null, items: readonly CaseOverviewOut[]): CustomerChoice | null {
  if (filter === null) return null;
  if (filter.displayName !== null) return { id: filter.id, displayName: filter.displayName };
  const found = items.find((item) => item.customer?.id === filter.id)?.customer;
  return { id: filter.id, displayName: found ? (found.company ?? found.displayName) : "Kunde" };
}

/** A field, a select, a text area or anything editable — where „/" is a character, not a shortcut. */
function typingIn(target: EventTarget | null): boolean {
  return target instanceof HTMLElement && (target.isContentEditable || target.closest("input, textarea, select") !== null);
}

/**
 * `/vorgaenge` — every Vorgang of mine (D-1), as a dense table (D-6).
 *
 * `includeFinished` only ever comes from the switch: the server hides
 * `rejected`/`cancelled`/`archived` by default, keeps `won`/`lost`, and lets an
 * explicit `status` filter win over the switch (Plan C, C-9) — so „Beendet"
 * as a stage filter finds its Vorgänge without the switch.
 */
export function RegisterScreen() {
  const navigate = useNavigate();
  const arrived = useRouteNotice();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  // Fix round 2, finding 7: where „Weitere zeigen" was when it is clicked —
  // read once the next answer actually has more rows than that, to focus
  // the first of them; never read during render (`react-hooks/refs`).
  const pendingFocusFrom = useRef<number | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [params] = useSearchParams();
  const [customerFilter, setCustomer] = useState<CustomerFilter | null>(() => customerFromUrl(params));
  const [sort, setSort] = useState<RegisterSort>("activity");
  const [showFinished, setShowFinished] = useState(false);
  const [limit, setLimit] = useState(PAGE_SIZE);
  // The sentence a navigation brought stands until the person does something here.
  const [touched, setTouched] = useState(false);
  const q = useDebouncedValue(search, SEARCH_DELAY_MS);
  const stage = STAGE_FILTERS.find((filter) => filter.value === stageFilter);

  // Asked first, so this mock call — and never the one below — is what a
  // test's `mock.lastCall` sees, the way it did before this query existed.
  const openOverview = useCaseOverview({ status: [...OPEN_STATUSES], limit: 1 });
  const overview = useCaseOverview({
    q,
    status: stage !== undefined ? [...stage.statuses] : undefined,
    customerId: customerFilter?.id,
    includeFinished: showFinished,
    sort,
    limit,
  });
  const changes = useStageChange();
  const page = overview.data;
  const customer = namedFilter(customerFilter, page?.items ?? []);
  const filtered = q.trim() !== "" || stage !== undefined || customer !== null;

  useDocumentTitle("Vorgänge");

  // UX ruling 12 (F-7): opening the register puts a person down on its
  // heading. Mount only — a new answer must not pull the focus back.
  useEffect(() => {
    headingRef.current?.focus();
  }, []);

  // UX ruling 4 (F-14): „/" puts the cursor in the search, from anywhere but a field.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== "/" || event.metaKey || event.ctrlKey || event.altKey) return;
      if (typingIn(event.target)) return;
      event.preventDefault();
      searchRef.current?.focus();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Fix round 2, finding 7: once the answer actually grew past where
  // „Weitere zeigen" stood, put focus on the first newly-revealed row
  // instead of leaving it to fall onto <body> when the button (the last
  // time) or the row it was next to (any other time) moves under the
  // pointer. Falls back to the search field if, somehow, there is no such
  // row — never leaves focus unhandled.
  useEffect(() => {
    const from = pendingFocusFrom.current;
    if (from === null || page === undefined || overview.isPlaceholderData === true) return;
    if (page.items.length <= from) return;
    pendingFocusFrom.current = null;
    const links = contentRef.current?.querySelectorAll<HTMLAnchorElement>('a[href^="/vorgang/"]');
    const target = links?.[from] ?? null;
    if (target !== null) target.focus();
    else searchRef.current?.focus();
  }, [page, overview.isPlaceholderData]);

  // A new situation — search, filter, reset — outdates whatever a previous
  // gesture had to say (fix round 1, I4: a refusal notice used to sit in the
  // status line forever) and starts again from the first page.
  function touch<T>(set: (value: T) => void) {
    return (value: T) => {
      setTouched(true);
      changes.clear();
      setLimit(PAGE_SIZE);
      set(value);
    };
  }

  function reset() {
    setTouched(true);
    changes.clear();
    setLimit(PAGE_SIZE);
    setSearch("");
    setStageFilter("");
    setCustomer(null);
    // M9: a reset that leaves „Beendete zeigen" on is only half a reset.
    setShowFinished(false);
    // Fix round 2, finding 7: the button this was clicked on is about to
    // disappear once real rows replace the no-hits state — move focus
    // before that happens instead of letting it fall onto <body>.
    searchRef.current?.focus();
  }

  // I5: while the next answer is only a placeholder, its `totalCount` is
  // the *previous* query's — pairing it with the *new* filters would print
  // „12 Treffer für „Sock"" when the real answer is 3. The line before the
  // change stands until a real answer for the new one lands.
  const computedLine =
    page !== undefined
      ? countLine(page, {
          q: q.trim(),
          stageLabel: stage?.label ?? null,
          customerName: customer?.displayName ?? null,
          showFinished,
          openCount: openOverview.data?.totalCount ?? null,
        })
      : null;
  // Neither a ref (this repository's lint refuses `ref.current` during
  // render, `react-hooks/refs`) nor a `setState` in an effect (flagged as a
  // cascading render, `react-hooks/set-state-in-effect`) — React's own
  // sanctioned way to remember the previous render's value is to adjust
  // state **during** render, guarded by a comparison, exactly as here: this
  // bails out immediately once `confirmedLine` already matches, so it is one
  // extra render pass while the answer is still fresh, not a loop.
  const [confirmedLine, setConfirmedLine] = useState<string | null>(computedLine);
  if (overview.isPlaceholderData !== true && computedLine !== confirmedLine) {
    setConfirmedLine(computedLine);
  }

  const unreachable = page === undefined && overview.isPending !== true;
  // „Filter zurücksetzen" (UX gate round 4, #9): from the first typed letter
  // — the raw field, not the debounced query — until everything is back.
  // Not while the frame below says „Suche und Filter zurücksetzen" with its
  // hint for a search that found nothing: one way, not two, on that screen.
  const narrowed = search.trim() !== "" || stageFilter !== "" || customerFilter !== null || showFinished;
  const frameResets = page !== undefined && page.items.length === 0 && (filtered || showFinished);
  const status =
    changes.notice ??
    (arrived !== null && !touched
      ? arrived
      : overview.isPlaceholderData === true
        ? (confirmedLine ?? "Sucht …")
        : (computedLine ??
          (overview.isPending ? "Vorgänge werden geladen …" : "Vorgänge nicht erreichbar — der Server antwortet gerade nicht.")));

  // Fix round 2, finding 7: the register's own limit never asks the server
  // for more than its route allows (`Query(..., le=500)`) — offering
  // „Weitere zeigen" past that would just repeat the same, already-capped
  // answer.
  function showMore() {
    pendingFocusFrom.current = page?.items.length ?? 0;
    setLimit((current) => Math.min(current + PAGE_SIZE, MAX_LIMIT));
  }

  const table = (empty?: ReactNode) => (
    <RegisterTable
      page={page ?? null}
      refreshing={overview.isPlaceholderData === true}
      sort={sort}
      onSort={touch(setSort)}
      busy={changes.busy}
      onStage={(id, to) => void changes.stage(id, to)}
      empty={empty}
      canShowMore={page !== undefined && page.items.length < page.totalCount && limit < MAX_LIMIT}
      onShowMore={showMore}
    />
  );

  let content: ReactNode;
  if (overview.isPending && page === undefined) {
    content = table();
  } else if (page === undefined) {
    // M4: the box below already says this in full — the status line, which
    // is the live region a screen reader hears, still carries the same
    // sentence for that announcement, but does not print it a second time.
    content = (
      <StateMessage title="Vorgänge nicht erreichbar" live={false}>
        Der Server antwortet gerade nicht.
        <div>
          <Button onClick={() => void overview.refetch()}>Erneut laden</Button>
        </div>
      </StateMessage>
    );
  } else if (page.items.length === 0 && !filtered && !showFinished) {
    // Fix round 4, M2: the count stays in sight in the status line (hiding
    // it moved the frame up by its height the moment the list ran empty);
    // the frame says what to do, not the count a second time.
    content = table(
      <div className={styles.empty}>
        <MeshMark />
        <div>
          <p className={styles.emptyTitle}>Leg einen Vorgang an.</p>
          <p className={styles.emptyText}>Lies auf der Startseite eine Zeichnung ein oder zeichne einen Umriss.</p>
          <div className={styles.emptyActions}>
            <Button onClick={() => navigate("/")}>Zur Startseite</Button>
            <Button variant="quiet" onClick={() => navigate("/umriss")}>
              Umriss zeichnen
            </Button>
          </div>
        </div>
      </div>,
    );
  } else if (page.items.length === 0) {
    // „0 Treffer für „…"" stands above, where the count stood while typing.
    content = table(
      <div className={styles.empty}>
        <div>
          <p className={styles.emptyTitle}>Versuch eine andere Schreibweise oder lös einen Filter.</p>
          <p className={styles.emptyText}>Gesucht wird in Kunde, Vorgangsnummer, E-Mail und Titel.</p>
          <div className={styles.emptyActions}>
            <Button onClick={reset}>Suche und Filter zurücksetzen</Button>
          </div>
        </div>
      </div>,
    );
  } else {
    content = table();
  }

  return (
    <div className={styles.screen}>
      {/* A place's title (UX-Gutachten 2, B-13): no kicker — „Meine
          Vorgänge" over „Vorgänge" said the same twice. */}
      <ScreenHead place title="Vorgänge" headingRef={headingRef} />

      <RegisterToolbar
        search={search}
        onSearch={touch(setSearch)}
        searchRef={searchRef}
        stageFilter={stageFilter}
        onStageFilter={touch(setStageFilter)}
        customer={customer}
        customers={customersOf(page?.items ?? [], customer)}
        onCustomer={touch(setCustomer)}
        sort={sort}
        onSort={touch(setSort)}
        showFinished={showFinished}
        onToggleFinished={() => {
          setTouched(true);
          changes.clear();
          setLimit(PAGE_SIZE);
          setShowFinished((shown) => !shown);
        }}
        status={status}
        statusVisuallyHidden={unreachable}
        onReset={narrowed && !frameResets ? reset : undefined}
      />

      <div ref={contentRef}>{content}</div>
    </div>
  );
}
